import { NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { avatarSrc } from "@/lib/api";
import { NotificationBell } from "@/components/NotificationBell";
import { GuidedTour, useTour } from "@/components/GuidedTour";
import { ROLE_LABELS } from "@/lib/constants";
import { currentSkin, NEUTRAL_ROLE_LABELS, newsletterTerms } from "@/lib/skin";
import {
  LayoutDashboard, FileText, PlusCircle, ListChecks, CalendarDays, BookOpen,
  Newspaper, BarChart3, Menu, X, LogOut, Radio, Inbox, Layers, HelpCircle, LifeBuoy, UserCog,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const MEMBER_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/submit/sunday-news", label: "Submit Sunday News", icon: PlusCircle, feature: "publication_builder" },
  { to: "/submit/comms-request", label: "Communications Request", icon: FileText },
  { to: "/submissions", label: "My Submissions", icon: ListChecks },
  { to: "/calendar", label: "Communications Calendar", icon: CalendarDays, feature: "calendar" },
  { to: "/guidelines", label: "Guidelines & Resources", icon: BookOpen },
  { to: "/help", label: "Help & Manuals", icon: LifeBuoy },
];

const ADMIN_NAV = [
  { to: "/admin", label: "Comms Dashboard", icon: LayoutDashboard },
  { to: "/submissions", label: "All Requests", icon: Inbox },
  { to: "/submit/comms-request", label: "Submit a Request", icon: FileText },
  { to: "/submit/sunday-news", label: "Submit Sunday News", icon: PlusCircle, feature: "publication_builder" },
  { to: "/issues", label: "Sunday News Builder", icon: Newspaper, feature: "publication_builder" },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, feature: "calendar" },
  { to: "/reports", label: "Reports", icon: BarChart3, feature: "advanced_reports", roles: ["comms_admin", "president", "vice_president"] },
  { to: "/settings", label: "Leadership Settings", icon: Layers },
  { to: "/users", label: "Members & Roles", icon: UserCog },
  { to: "/guidelines", label: "Guidelines", icon: BookOpen },
  { to: "/help", label: "Help & Manuals", icon: LifeBuoy },
];

const COMMS_TEAM_NAV = [
  { to: "/admin", label: "Comms Dashboard", icon: LayoutDashboard },
  { to: "/submissions", label: "All Requests", icon: Inbox },
  { to: "/submit/comms-request", label: "Submit a Request", icon: FileText },
  { to: "/submit/sunday-news", label: "Submit Sunday News", icon: PlusCircle, feature: "publication_builder" },
  { to: "/issues", label: "Sunday News Builder", icon: Newspaper, feature: "publication_builder" },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, feature: "calendar" },
  { to: "/guidelines", label: "Guidelines", icon: BookOpen },
  { to: "/help", label: "Help & Manuals", icon: LifeBuoy },
];

const LEADER_NAV = [
  { to: "/admin", label: "Leadership Review", icon: LayoutDashboard },
  { to: "/submissions", label: "All Requests", icon: Inbox },
  { to: "/submit/comms-request", label: "Submit a Request", icon: FileText },
  { to: "/submit/sunday-news", label: "Submit Sunday News", icon: PlusCircle, feature: "publication_builder" },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, feature: "calendar" },
  { to: "/reports", label: "Reports", icon: BarChart3, feature: "advanced_reports", roles: ["comms_admin", "president", "vice_president"] },
  { to: "/settings", label: "Approval Settings", icon: Layers },
  { to: "/users", label: "Members & Roles", icon: UserCog },
  { to: "/help", label: "Help & Manuals", icon: LifeBuoy },
];

export const Layout = ({ children }) => {
  const { user, logout, features } = useAuth();
  const { startTour } = useTour();
  const [open, setOpen] = useState(false);
  const baseNav = (user?.role === "comms_admin" || user?.role === "technology") ? ADMIN_NAV : (user?.role === "comms_team" || user?.role === "pr_chairman") ? COMMS_TEAM_NAV : (user?.role === "president" || user?.role === "vice_president") ? LEADER_NAV : MEMBER_NAV;
  const terms = newsletterTerms();
  const relabel = { "/issues": terms.builder, "/submit/sunday-news": terms.submit };
  const nav = baseNav
    .filter((item) => !item.feature || features[item.feature])
    .filter((item) => !item.roles || item.roles.includes(user?.role))
    .map((item) => (relabel[item.to] ? { ...item, label: relabel[item.to] } : item));
  const isMember = user?.role === "member";

  const initials = (user?.name || "U").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
  const roleLabels = currentSkin() === "neutral" ? NEUTRAL_ROLE_LABELS : ROLE_LABELS;

  return (
    <div className="flex min-h-screen bg-background">
      {open && <div className="fixed inset-0 z-30 bg-foreground/40 lg:hidden" onClick={() => setOpen(false)} />}
      <aside
        className={`fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-border bg-card transition-transform lg:static lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div className="flex items-center gap-2 border-b border-border px-6 py-5">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Radio className="h-5 w-5" />
          </div>
          <div>
            <p className="font-heading text-lg font-bold leading-none tracking-tight">CommConnect</p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{roleLabels[user?.role]}</p>
          </div>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto p-3 cc-scroll">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setOpen(false)}
              data-testid={`nav-${item.to.replace(/\//g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:bg-secondary hover:text-foreground"
                }`
              }
              end={item.to === "/dashboard" || item.to === "/admin"}
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-border p-3">
          <NavLink to="/profile" onClick={() => setOpen(false)} data-testid="nav-profile"
            className="flex items-center gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-secondary">
            <Avatar className="h-9 w-9">
              <AvatarImage src={avatarSrc(user?.picture)} />
              <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">{initials}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold">{user?.name}</p>
              <p className="truncate text-xs text-muted-foreground">{user?.committee || user?.email}</p>
            </div>
          </NavLink>
          <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-btn" className="mt-1 w-full justify-start text-muted-foreground">
            <LogOut className="mr-2 h-4 w-4" /> Sign out
          </Button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex items-center justify-between border-b border-border/40 bg-background/80 px-4 py-3 backdrop-blur-xl backdrop-saturate-150 lg:px-8">
          <div className="flex items-center gap-2">
            <button className="rounded-lg p-2 hover:bg-secondary lg:hidden" onClick={() => setOpen(true)} data-testid="mobile-menu-btn">
              <Menu className="h-5 w-5" />
            </button>
            <p className="font-heading text-sm text-muted-foreground hidden sm:block">One portal. Every request. Clearer communication.</p>
          </div>
          <div className="flex items-center gap-1">
            {isMember && features.publication_builder && (
              <Button size="sm" className="mr-2 hidden rounded-full sm:flex" onClick={() => (window.location.href = "/submit/sunday-news")} data-testid="header-new-btn">
                <PlusCircle className="mr-1.5 h-4 w-4" /> {terms.submit}
              </Button>
            )}
            <button onClick={() => startTour()} data-testid="tour-help-btn" title="Take a guided tour"
              className="flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
              <HelpCircle className="h-5 w-5" />
            </button>
            <NotificationBell />
          </div>
        </header>
        <main className="flex-1 px-4 py-6 lg:px-8 lg:py-8">
          {user?.is_demo && (
            <div data-testid="demo-banner" className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/30 bg-primary/10 px-4 py-3 text-sm">
              <span className="font-medium text-foreground">🔒 Read-only demo — feel free to explore; changes are disabled.</span>
              <button onClick={() => { localStorage.removeItem("cc_token"); localStorage.setItem("cc_skin", "aka"); window.location.href = "/home"; }}
                className="rounded-full bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground">Exit demo</button>
            </div>
          )}
          {children}
        </main>
      </div>
    </div>
  );
};
